import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {Promise<void> | null} */
  #refreshPromise = null;

  /** @type {number} */
  #lastRefreshAt = 0;

  /** @type {number} */
  #hiddenAt = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    window.addEventListener('pageshow', this.#handlePageShow);
    window.addEventListener('focus', this.#handleWindowFocus);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    document.addEventListener('ysn:cart-refresh-request', this.#handleRefreshRequest);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    window.removeEventListener('pageshow', this.#handlePageShow);
    window.removeEventListener('focus', this.#handleWindowFocus);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    document.removeEventListener('ysn:cart-refresh-request', this.#handleRefreshRequest);
    this.#historyAbortController?.abort();
  }

  #handleRefreshRequest = () => {
    this.#refreshCartFromServer(true);
  };

  #handlePageShow = (event) => {
    // Checkout back-navigation is commonly restored from the browser's BFCache.
    // Its DOM can be older than Shopify's live cart, so reconcile immediately.
    const navigation = performance.getEntriesByType?.('navigation')?.[0];
    const isBackForward = navigation?.type === 'back_forward';
    if (event.persisted || isBackForward) {
      this.#refreshCartFromServer(true);
    }
  };

  #handleWindowFocus = () => {
    if (Date.now() - this.#lastRefreshAt > 1500) {
      this.#refreshCartFromServer(false);
    }
  };

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.#hiddenAt = Date.now();
      return;
    }

    // Reconcile after returning from another tab/page (including checkout).
    if (!this.#hiddenAt || Date.now() - this.#hiddenAt > 500) {
      this.#refreshCartFromServer(false);
    }
    this.#hiddenAt = 0;
  };

  /**
   * Fetches an authoritative cart snapshot and a fresh server-rendered header
   * section, then replaces the drawer's inner contents wholesale. A wholesale
   * replacement is intentional: positional DOM morphing can reuse stale line
   * rows after checkout/back navigation and display duplicated or missing items.
   * @param {boolean} [force]
   */
  async #refreshCartFromServer(force = false) {
    if (this.#refreshPromise) return this.#refreshPromise;
    if (!force && Date.now() - this.#lastRefreshAt < 600) return;

    const cartItems = this.querySelector('cart-items-component[data-section-id]');
    const sectionId = cartItems?.dataset.sectionId;
    if (!sectionId) return;

    this.#refreshPromise = (async () => {
      this.setAttribute('cart-refreshing', '');

      try {
        const sectionUrl = new URL(window.location.href);
        sectionUrl.searchParams.set('section_id', sectionId.replace(/^shopify-section-/, ''));
        sectionUrl.searchParams.set('_ysn_cart_refresh', String(Date.now()));

        const cartBase = Theme.routes.cart_url || '/cart';
        const cartUrl = cartBase.endsWith('.js') ? cartBase : `${cartBase}.js`;

        const [sectionResponse, cartResponse] = await Promise.all([
          fetch(sectionUrl.toString(), {
            cache: 'no-store',
            credentials: 'same-origin',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          }),
          fetch(cartUrl, {
            cache: 'no-store',
            credentials: 'same-origin',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          }),
        ]);

        if (!sectionResponse.ok || !cartResponse.ok) throw new Error('Unable to refresh cart');

        const [sectionHTML, cart] = await Promise.all([sectionResponse.text(), cartResponse.json()]);
        await customElements.whenDefined('cart-items-component');

        // Reconcile only the cart data-bearing regions. The premium drawer shell,
        // header, guarantee/timer area, tracking elements, sticky summary wrapper,
        // spacing and every theme-editor setting remain the exact live DOM nodes.
        // Replacing the complete drawer subtree would reset those presentation
        // details even though the cart data itself was corrected.
        const reconciled =
          typeof cartItems.reconcileFromSectionHTML === 'function'
            ? cartItems.reconcileFromSectionHTML(sectionHTML)
            : false;

        if (!reconciled) {
          console.warn('[YSN cart reconciliation] Fresh cart markup could not be applied; existing drawer preserved.');
        }

        const dialog = this.refs.dialog;
        if (dialog instanceof HTMLDialogElement) {
          dialog.classList.toggle('cart-drawer--empty', Number(cart.item_count || 0) === 0);
        }

        // The icon must come from the same live cart snapshot, never from stale
        // session storage restored by the browser.
        for (const icon of document.querySelectorAll('cart-icon')) {
          if ('renderCartBubble' in icon && typeof icon.renderCartBubble === 'function') {
            icon.renderCartBubble(Number(cart.item_count || 0), false, false);
          }
        }

        this.#lastRefreshAt = Date.now();
        this.#updateStickyState();
        document.dispatchEvent(
          new CustomEvent('ysn:cart-reconciled', {
            detail: { itemCount: Number(cart.item_count || 0), cart },
          })
        );
      } catch (error) {
        console.error('[YSN cart reconciliation]', error);
      } finally {
        this.removeAttribute('cart-refreshing');
        this.#refreshPromise = null;
      }
    })();

    return this.#refreshPromise;
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  async open() {
    // Open immediately for responsiveness, then replace stale BFCache markup
    // before the customer can mutate a potentially outdated line number.
    this.showDialog();
    await this.#refreshCartFromServer(true);

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  /**
   * Synchronizes the drawer state after an empty/non-empty cart transition.
   * The first item can be rendered after the drawer has already opened, so the
   * server-rendered `cart-drawer--empty` class and sticky-summary state must be
   * corrected without rebuilding or restyling the drawer.
   */
  refreshPresentationState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!(dialog instanceof HTMLDialogElement)) return;

    const cartItems = this.querySelector('cart-items-component[data-drawer]');
    const hasCart = cartItems?.querySelector('.cart-form') instanceof HTMLElement;

    dialog.classList.toggle('cart-drawer--empty', !hasCart);
    dialog.setAttribute('aria-labelledby', hasCart ? 'cart-drawer-heading' : 'cart-drawer-heading-empty');

    // Wait for the newly inserted cart rows/summary to receive their final
    // layout before calculating whether the summary should remain sticky.
    requestAnimationFrame(() => this.#updateStickyState());
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
